import React, { Fragment } from 'react'
import {
  ChecklistPhase,
  getChecklistItemTypeLabel,
  IChecklistItem,
} from '@oneethos/shared'
import ChecklistItemForm from './checklist-item-form'
import './checklist-items-list.scss'

const PHASE_LABELS: Record<ChecklistPhase, string> = {
  [ChecklistPhase.LoanApplication]: 'Loan Application',
  [ChecklistPhase.LoanFunding]: 'Loan Funding',
}

type ChecklistItemsListProps = {
  checklistItems: IChecklistItem[]
  editingItem: IChecklistItem | null
  isAdding: boolean
  newItem: Partial<IChecklistItem>
  setNewItem: (item: Partial<IChecklistItem>) => void
  saving: boolean
  onToggleItem: (item: IChecklistItem) => void
  onStartAdding: () => void
  onSave: () => void
  onEdit: () => void
  onCancel: () => void
}

const ChecklistItemsList = ({
  checklistItems,
  editingItem,
  isAdding,
  newItem,
  setNewItem,
  saving,
  onToggleItem,
  onStartAdding,
  onSave,
  onEdit,
  onCancel,
}: ChecklistItemsListProps) => {
  const itemsSortedBySequence = [...checklistItems].sort((a, b) => a.sequence - b.sequence)
  const isAddingNewItem = isAdding && !editingItem

  const handleRowActivate = (item: IChecklistItem) => {
    if (saving) return
    onToggleItem(item)
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-dark">Click a row to edit or view details for a given item.</span>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onStartAdding}
          disabled={isAddingNewItem || saving}
        >
          Add Checklist Item
        </button>
      </div>
      {isAddingNewItem && (
        <div className="border rounded-3 mb-3 bg-light">
          <ChecklistItemForm
            newItem={newItem}
            setNewItem={setNewItem}
            editingItem={null}
            saving={saving}
            onSave={onSave}
            onEdit={onEdit}
            onCancel={onCancel}
          />
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-hover align-middle checklist-items-table">
          <thead>
            <tr>
              <th>Seq</th>
              <th>Name</th>
              <th>Phase</th>
              <th>Type</th>
              <th>Fund Release Types</th>
              <th>Owner</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {itemsSortedBySequence.map(item => {
              const isExpanded = editingItem?.id === item.id

              return (
                <Fragment key={item.id}>
                  <tr
                    className={`checklist-item-row pointer ${isExpanded ? 'table-active' : ''}`}
                    onClick={() => handleRowActivate(item)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleRowActivate(item)
                      }
                    }}
                    tabIndex={0}
                    aria-expanded={isExpanded}
                  >
                    <td>{item.sequence}</td>
                    <td>{item.label}</td>
                    <td>{PHASE_LABELS[item.phase] || item.phase}</td>
                    <td>{getChecklistItemTypeLabel(item)}</td>
                    <td>
                      {item.fundReleaseTypes?.map((type, idx) => (
                        <span key={idx} className="badge rounded-pill bg-secondary me-1">
                          {type.split(' ')[0]}
                        </span>
                      ))}
                    </td>
                    <td className="text-capitalize">{item.owner}</td>
                    <td>{item.active ? 'Yes' : 'No'}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-light">
                        <div onClick={event => event.stopPropagation()}>
                          <ChecklistItemForm
                            newItem={newItem}
                            setNewItem={setNewItem}
                            editingItem={editingItem}
                            saving={saving}
                            onSave={onSave}
                            onEdit={onEdit}
                            onCancel={onCancel}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {checklistItems.length === 0 && !isAddingNewItem && (
        <div className="text-center text-muted py-4">
          <p>No checklist items found.</p>
        </div>
      )}
    </>
  )
}

export default ChecklistItemsList
